import React from "react";
import { Box, HStack, VStack } from "../../components/utils";
import BCHeader from "../../shared/BCHeader";
import GenericTableHeader from "../../shared/GenericTableHeader";
import ReportsAgGridTable from "./ReportsAgGridTable";
import { CustomResponse, ReportDto } from "../../types/Grant";
import {
  downloadPoolStatement,
  generateAcceptanceReport,
  generateCancellationReport,
  generateCashoutReport,
  generateEmployeeDetailsReport,
  generateExerciseReport,
  generateFinancialReport,
  generateGrantReport,
  generateLiquidityReport,
  generateSebiReport,
  generateTenCReport,
  generateTransactionReport,
  generateVestWiseReport,
} from "../../api/Esop";
import { useCompanyStore } from "../../store";
import { HissaReportType } from "../../types/common";

export interface ReportNames {
  name: string;
  description: string;
  type: string;
  headerName: string;
  startDate: string;
  endDate: string;
  reportType?: HissaReportType;
  onSubmitFunction?: (param: any) => Promise<CustomResponse<ReportDto>>;
}

const reportNames: ReportNames[] = [
  {
    name: "Grant Report",
    description: "Provides details of all grants issued under the ESOP plan.",
    type: "Grant",
    headerName: "Download Grant Report",
    reportType: HissaReportType.GRANT_REPORT,
    onSubmitFunction: generateGrantReport,
    startDate: "Grant Start Date",
    endDate: "Grant End Date",
  },
  {
    name: "Vest-wise Report",
    description: "Provides details of all grants issued under the ESOP plan.",
    type: "Vesting",
    headerName: "Download Vestwise Report",
    reportType: HissaReportType.VEST_WISE_REPORT,
    onSubmitFunction: generateVestWiseReport,
    startDate: "Grant Start Date",
    endDate: "Grant End Date",
  },
  {
    name: "Cancellation Report",
    description: "Tracks the cancellation of any ESOP grants or shares.",
    type: "Transaction",
    headerName: "Download Cancellation Report",
    reportType: HissaReportType.CANCELLATION_REPORT,
    onSubmitFunction: generateCancellationReport,
    startDate: "Transaction Start Date",
    endDate: "Transaction End Date",
  },
  {
    name: "Employee wise Report",
    description: "Offers a breakdown of ESOP details by employee.",
    type: "Employee",
    reportType: HissaReportType.EMPLOYEE_WISE_REPORT,
    headerName: "Download Employee wise Report",
    onSubmitFunction: generateEmployeeDetailsReport,
    startDate: "Transaction Start Date",
    endDate: "Transaction End Date",
  },
  {
    name: "Exercise Report",
    description: "Summarizes the exercise of vested options by employees.",
    type: "Transaction",
    reportType: HissaReportType.EXERCISE_REPORT,
    headerName: "Download Exercise Report",
    onSubmitFunction: generateExerciseReport,
    startDate: "Transaction Start Date",
    endDate: "Transaction End Date",
  },
  {
    name: "Cashout Report",
    description: "Summarizes the cashout of vested options by employees.",
    type: "Transaction",
    reportType: HissaReportType.CASHOUT_REPORT,
    headerName: "Download Cashout Report",
    onSubmitFunction: generateCashoutReport,
    startDate: "Transaction Start Date",
    endDate: "Transaction End Date",
  },
  {
    name: "Liquidity Report",
    description: "Displays liquidity events and associated stock movements.",
    type: "Grant",
    headerName: "Download Liquidity Report",
    reportType: HissaReportType.LIQUIDITY_REPORT,
    onSubmitFunction: generateLiquidityReport,
    startDate: "Event Start Date",
    endDate: "Event End Date",
  },
  {
    name: "BuyBack Report",
    description: "Details any buyback events of employee shares.",
    type: "Grant",
    reportType: HissaReportType.BUYBACK_REPORT,
    headerName: "Download BuyBack Report",
    onSubmitFunction: generateLiquidityReport,
    startDate: "Event Start Date",
    endDate: "Event End Date",
  },
  {
    name: "Pool Statement",
    description: "Shows the available and used ESOP pool balance.",
    type: "Company",
    reportType: HissaReportType.POOL_STATEMENT,
    headerName: "Download Pool Statement",
    onSubmitFunction: downloadPoolStatement,
    startDate: "",
    endDate: "",
  },
  {
    name: "Acceptance Report",
    description: "Documents the acceptance of ESOP offers by employees.",
    type: "Grant",
    reportType: HissaReportType.ACCEPTANCE_REPORT,
    headerName: "Download Acceptance Report",
    onSubmitFunction: generateAcceptanceReport,
    startDate: "Grant Start Date",
    endDate: "Grant End Date",
  },
  {
    name: "Financial Report",
    description: "Provides financial implications and status of the ESOP plan.",
    type: "Grant",
    reportType: HissaReportType.FINANCIAL_REPORT,
    headerName: "Download Financial Report",
    onSubmitFunction: generateFinancialReport,
    startDate: "Grant Start Date",
    endDate: "Grant End Date",
  },
  {
    name: "SH-6 Report",
    description: "Regulatory report for managing ESOP compliance.",
    type: "Grant",
    reportType: HissaReportType.SH6_REPORT,
    headerName: "Download SH-6 Report",
    startDate: "",
    endDate: "",
  },
  {
    name: "Employee Data Sync history",
    description:
      "Comprehensive Report on Employee Data Synchronization History from External Data Sources",
    type: "Employee",
    headerName: "Download Sync History Report",
    startDate: "",
    endDate: "",
  },
  {
    name: "ESOP Accounting Report",
    description: "Compensation Report For Company",
    type: "Grant",
    reportType: HissaReportType.ESOP_ACCOUNTING,
    headerName: "Download ESOP Accounting Report",
    startDate: "",
    endDate: "",
  },
  {
    name: "Section 10-C Report",
    description: "Section 10-C Report for ESOP Exercise",
    type: "Grant",
    reportType: HissaReportType.SECTION_10_C_REPORT,
    headerName: "Download Section 10-C Report",
    onSubmitFunction: generateTenCReport,
    startDate: "From Date",
    endDate: "To Date",
  },
];

const Reports = () => {
  const { companyData } = useCompanyStore();
  if (companyData?.companyMetaData.typeOfTheCompany === "PUBLIC") {
    if (!reportNames.find((report) => report.name === "SEBI Report")) {
      reportNames.push({
        name: "SEBI Report",
        description: "SEBI Report for ESOP Exercise",
        type: "Plan",
        reportType: HissaReportType.SEBI_REPORT,
        headerName: "Download SEBI Report",
        startDate: "From Date",
        endDate: "To Date",
        onSubmitFunction: generateSebiReport,
      });
    }
  }
  return (
    <VStack className="gap-4">
      <HStack className="justify-between min-h-[45px] items-center">
        <BCHeader
          className="items-baseline "
          bcTitle="Reports"
          bcSubTitle={""}
        />
      </HStack>
      <Box className="p-4 bg-white border rounded-lg min-w-min border-borderColor drop-shadow-box ">
        <HStack className="justify-between w-full ">
          <ReportsAgGridTable reportsData={reportNames} />
        </HStack>
      </Box>
    </VStack>
  );
};

export default Reports;
